/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Open Source Version is distributed with Sencha Ext JS pursuant to a FLOSS Exception agreed upon
 * between Sonatype, Inc. and Sencha Inc. Sencha Ext JS is licensed under GPL v3 and cannot be redistributed as part of a
 * closed source work.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

/**
 * Shiro wildcard permission matching, ported from NX.Permissions.implies() in nexus-rapture.
 * @param {string} granted
 * @param {string} expected
 * @returns {boolean} true if the granted permission implies the expected one
 */
export function implies(granted, expected) {
  const grantedParts = granted.split(':');
  const expectedParts = expected.split(':');
  let i;

  for (i = 0; i < expectedParts.length; i++) {
    // fewer granted parts: everything after them is implied
    if (i >= grantedParts.length) {
      return true;
    }
    const grantedSubParts = grantedParts[i].split(',');
    const expectedSubParts = expectedParts[i].split(',');
    if (!grantedSubParts.includes('*') && !expectedSubParts.every((part) => grantedSubParts.includes(part))) {
      return false;
    }
  }

  // more granted parts: only implied if they are all wildcards
  for (; i < grantedParts.length; i++) {
    if (!grantedParts[i].split(',').includes('*')) {
      return false;
    }
  }

  return true;
}

/**
 * Creates a permission check over one set of granted permissions, with the same semantics as NX.Permissions.check():
 * the permission is granted if it is in the set or any granted permission implies it. Results are cached for the
 * lifetime of the set.
 * @param {Set<string>|undefined} permissions the lower-cased ids of the granted permissions, or undefined when not loaded
 * @returns {function(string): boolean}
 */
export function createPermissionCheck(permissions) {
  const cache = new Map();

  return (permission) => {
    if (!permissions) {
      return false;
    }

    const expected = permission.toLowerCase();
    if (permissions.has(expected)) {
      return true;
    }
    if (cache.has(expected)) {
      return cache.get(expected);
    }

    const result = Array.from(permissions).some((granted) => implies(granted, expected));
    cache.set(expected, result);
    return result;
  };
}
